import { Component, ElementRef, Inject, inject, OnInit, Signal, signal, viewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, } from '@angular/material/dialog';
import { JsReportService, LicenseService } from '../../../core/services';
import { FilterFieldMap, LicenseWithCompletionResource, PayloadResponse, ToggleDownloadCertificateLicenseDto } from '../../../core/models';
import { FilterBarComponent } from '../filter-bar/filter-bar.component';
import { CustomPaginationComponent } from '../custom-pagination/custom-pagination.component';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { CommonModule } from '@angular/common';
import { LicenseStatus } from '../../../shared/enum/license-status';
import { concatMap, Observable, tap } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { DialogService } from '../../../shared/services';
import { ExtendLicenseDialogComponent } from '../extend-license-dialog/extend-license-dialog.component';
import { ProgressBarComponent } from '../progress-bar/progress-bar.component';

@Component({
  selector: 'app-dialog-massive-actions',
  imports: [
    MatButtonModule,
    FilterBarComponent,
    CustomPaginationComponent,
    SpinnerComponent,
    CommonModule,
    FormsModule,
    ProgressBarComponent
  ],
  templateUrl: './dialog-massive-actions.component.html',
  styleUrl: './dialog-massive-actions.component.scss'
})
export class DialogMassiveActionsComponent implements OnInit {
  private licenseService = inject(LicenseService);
  private dialogService = inject(DialogService);
  private jsReportService = inject(JsReportService);
  initialFocus = viewChild<ElementRef>('initialFocus');

  selectedOption = signal<string | null>(null);
  arrayLicenseSignal = signal<LicenseWithCompletionResource[]>([]);
  status = LicenseStatus;
  filters!: Signal<FilterFieldMap>;
  licenseList = signal<any>([]);
  currentPage = signal<number>(1);
  itemsPerPage = signal<number>(5);
  totalItems = signal<number>(0);
  totalPages = signal<number>(0);
  isAdmin!: Signal<boolean>;

  onFilterChanged!: (filters: FilterFieldMap) => void;
  onPagedItemsChange!: (page: number) => void;
  onPageSizeChanged!: (size: number) => void;

  getLicenses(): Observable<PayloadResponse<LicenseWithCompletionResource[]>> {
    return this.licenseService.getLicensesList(this.filters(), this.currentPage(), this.itemsPerPage()).pipe(
      tap(res => {
        this.licenseList.set(res.data);
        this.currentPage.set(res.metadata?.['currentPage']);
        this.itemsPerPage.set(res.metadata?.['itemsPerPage']);
        this.totalItems.set(res.metadata?.['totalItems']);
        this.totalPages.set(res.metadata?.['totalPages']);
        this.resetLicense();
      }),
    )
  }

  constructor(@Inject(MAT_DIALOG_DATA) public data: any) { }

  ngOnInit(): void {
    this.isAdmin = this.data.isAdmin;
    this.filters = this.data.filters;
    this.licenseList = this.data.licenseList;
    this.currentPage = this.data.currentPage;
    this.itemsPerPage = this.data.itemsPerPage;
    this.totalItems = this.data.totalItems;
    this.totalPages = this.data.totalPages;

    this.onFilterChanged = this.data.onFilterChanged;
    this.onPagedItemsChange = this.data.onPagedItemsChange;
    this.onPageSizeChanged = this.data.onPageSizeChanged;
  }

  // Massive actions
  addRemoveLicenseId(license: LicenseWithCompletionResource) {

    const isLicenseAlreadyPresent = this.arrayLicenseSignal().some(
      lic => lic.licenseId === license.licenseId
    );

    if (isLicenseAlreadyPresent) {
      this.arrayLicenseSignal.set(
        this.arrayLicenseSignal().filter(lic => lic.licenseId !== license.licenseId)
      );
    } else {
      this.arrayLicenseSignal.set([...this.arrayLicenseSignal(), license]);
    }

  }

  massiveActionsMap: { [key: string]: () => void } = {
    extend: () => this.extendLicense(),
    enable: () => this.toggleCertificate(true),
    disable: () => this.toggleCertificate(false),
    download: () => this.downloadCertificate()
  };

  applyMassiveActions() {
    if (this.arrayLicenseSignal().length === 0) {
      this.dialogService.openDialogError('Attenzione', 'Selezionare almeno una licenza!');
      return;
    }
    this.massiveActionsMap[this.selectedOption() as string]?.();
  }

  extendLicense(): void {

    const arrLicenseIds = this.arrayLicenseSignal()
      .filter(lic => lic.status === LicenseStatus.ATTIVO);

    if (arrLicenseIds.length === 0) {
      this.dialogService.openDialogError('Attenzione', 'Selezionare almeno una licenza attiva!');
      return;
    }

    this.dialogService.dialog.open(ExtendLicenseDialogComponent, {
      disableClose: true,
      width: '500px',
      data: {
        arrayLicenseSignal: arrLicenseIds,
        onSubmitted: () => {
          this.getLicenses().subscribe({
            next: () => {
              this.dialogService.openDialogAlert('Aggiornamento Completato', 'Le licenze sono state aggiornate con successo!', () => {
              });
            },
            error: (err) => {
              console.error("Errore durante il ricaricamento delle licenze:", err);
              this.dialogService.openDialogError('Errore di Aggiornamento', 'Si è verificato un errore durante il ricaricamento delle licenze dopo l\'estensione.');
            }
          });
        }
      }
    });
    return;
  }

  toggleCertificate(enable: boolean): void {
    const title = enable ? 'Abilita attestato' : 'Disabilita attestato';
    const message = enable
      ? 'Abilitare gli attestati selezionati?'
      : 'Disabilitare gli attestati selezionati?';
    const confirmButton = enable ? 'Abilita' : 'Disabilita';

    const arrLicenseIds = this.arrayLicenseSignal()
      .filter(lic => lic.status === LicenseStatus.DOWNLOAD)
      .map(lic => lic.licenseId);

    if (arrLicenseIds.length === 0) {
      this.dialogService.openDialogError('Attenzione', 'Selezionare almeno una licenza Download!');
      return;
    }

    this.dialogService.openDialogConfirm(title, message, true, 'Annulla', confirmButton, () => {

      const toggleEnableDownloadCertificate: ToggleDownloadCertificateLicenseDto = {
        arrLicenseIds,
        enable
      };

      this.licenseService.toggleEnableDownloadCertificate(toggleEnableDownloadCertificate).pipe(
        concatMap(() => this.getLicenses())
      ).subscribe({
        next: () => {
          const successTitle = 'Completato';
          const successMessage = enable
            ? 'Gli attestati sono stati abilitati con successo!'
            : 'Gli attestati sono stati disabilitati con successo!';
          this.dialogService.openDialogInfoNoRedirect(successTitle, successMessage);
        },
        error: (err) => {
          console.error(`Errore durante la ${enable ? 'abilitazione' : 'disabilitazione'} o il ricaricamento degli attestati:`, err);
          const errorTitle = 'Errore';
          const errorMessage = `Si è verificato un errore durante la ${enable ? 'abilitazione' : 'disabilitazione'} degli attestati.`;
          this.dialogService.openDialogError(errorTitle, errorMessage);
        }
      });
    });
  }

  downloadCertificate(): void {
    const arrLicenseIds = this.arrayLicenseSignal()
      .filter(lic => lic.status === LicenseStatus.DOWNLOAD)
      .map(lic => lic.licenseId);

    if (arrLicenseIds.length === 0) {
      this.dialogService.openDialogError('Attenzione', 'Selezionare almeno una licenza Download!');
      return;
    }
    
    if(arrLicenseIds.length > 50){
      this.dialogService.openDialogError('Attenzione', 'La quantità massima di attestati scaricabili per volta è pari a 50!');
      return;
    }

    this.dialogService.openDialogConfirm('Scarica attestato', 'Scaricare gli attestati selezionati?', true, 'Annulla', 'Scarica', () => {

      this.jsReportService.getJsReportAllDocuments(arrLicenseIds)
        .subscribe({
          next: (res) => {
            this.jsReportService.downloadPdfFromResponse(res);
          },
          error: (err) => {
            console.error("Errore durante lo scaricamento degli attestati:", err);
            this.dialogService.openDialogError('Errore in download', 'Si è verificato un errore durante il download degli attestati.');
          }
        });
    });
  }


  toggleSelectAll() {
    const currentLicenses = this.licenseList();
    const allSelected = currentLicenses.every((lic: any) =>
      this.arrayLicenseSignal().some(sel => sel.licenseId === lic.licenseId)
    );

    if (allSelected) {
      const idsToRemove = currentLicenses.map((lic: any) => lic.licenseId);
      this.arrayLicenseSignal.set(
        this.arrayLicenseSignal().filter(lic => !idsToRemove.includes(lic.licenseId))
      );
    } else {
      const toAdd = currentLicenses.filter((lic: any) =>
        !this.arrayLicenseSignal().some(sel => sel.licenseId === lic.licenseId)
      );
      this.arrayLicenseSignal.set([
        ...this.arrayLicenseSignal(),
        ...toAdd
      ]);
    }
  }

  get isAllCurrentPageSelected(): boolean {
    const currentLicenses = this.licenseList();
    if (currentLicenses.length === 0) return false;

    return currentLicenses.every((license: any) =>
      this.arrayLicenseSignal().some(selected => selected.licenseId === license.licenseId)
    );
  }

  get isSomeCurrentPageSelected(): boolean {
    const currentLicenses = this.licenseList();
    if (currentLicenses.length === 0) return false;

    const selectedCount = currentLicenses.filter((license: any) =>
      this.arrayLicenseSignal().some(selected => selected.licenseId === license.licenseId)
    ).length;

    return selectedCount > 0 && selectedCount < currentLicenses.length;
  }

  isLicenseSelected(licenseId: number): boolean {
    return this.arrayLicenseSignal().some(lic => lic.licenseId === licenseId);
  }

  toggleLicenseSelection(license: LicenseWithCompletionResource) {
    const isSelected = this.isLicenseSelected(license.licenseId);
    if (isSelected) {
      this.arrayLicenseSignal.set(
        this.arrayLicenseSignal().filter(lic => lic.licenseId !== license.licenseId)
      );
    } else {
      this.arrayLicenseSignal.set([
        ...this.arrayLicenseSignal(),
        license
      ]);
    }
  }

  resetLicense(){
    this.arrayLicenseSignal.set([]);
  }

}
